import { useMemo, useState, type CSSProperties } from "react";
import { Banner, Btn, Card, Checkbox, Chip, Dropdown, Icon, Input, SegmentedControl, T, toneColors } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { EmptyState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { useNodeKinds } from "./useNodes";

import { useT, t as i18nT } from "@v0y4ge/i18n-runtime";
/**
 * Attributes — the tenant's attribute VOCABULARY, and its applicability.
 *
 * Built 2026-08-23 against the JAI-930 wiring kit @ `a040a1d4`
 * (`packages/api-contracts/src/custom-fields.ts` is the authority).
 *
 * ── RULE 2, WHICH THIS SCREEN EXISTS TO NOT BREAK ──────────────────────────
 *
 * Attributes DESCRIBE, they never SCOPE. There is no filter here that narrows
 * what a user may administer by an attribute value, and there never should be:
 * scope is the tree's materialized `path` prefix and only the tree. A screen
 * that filtered administrable nodes by an attribute would have invented a
 * second, weaker authorization model beside the real one.
 *
 * ── THE THREE THINGS THE UI HAS TO CARRY (§3.1) ────────────────────────────
 *
 * 1. **`multiselect` and `radio` are not field types.** Several values is
 *    `cardinality: 'many'`, INDEPENDENT of type — so several free-text values
 *    are now expressible, which `multiselect` could never say. Radio versus
 *    dropdown is a rendering choice, not a data type. Hence ten types plus a
 *    separate one-or-many control.
 * 2. **Options are rows with stable keys**, not JSON — the key is what values
 *    point at, what filters match and what translations hang off. Labels are
 *    freely editable; keys are not. The whole list is replaced on write.
 * 3. **An empty applicability relation means the definition applies to
 *    NOTHING**, never "applies to all". This is the one that most needs UI
 *    help: under an implicit-all reading, removing the last link from a REQUIRED
 *    definition silently widens it across the tenant. So "nothing selected" is
 *    rendered as a real, visible, deliberately uncomfortable state.
 *
 * ── §2 COMPONENT NOTE: THE COMPOSITION ATTEMPT ─────────────────────────────
 *
 * You asked to see the attempt before deciding whether to raise a component.
 * Both editors here are composed from existing primitives:
 *
 *  · The **applicability editor** is a `Checkbox` list over the tenant's kinds
 *    rather than a new multi-select widget. It works, and it carries the empty
 *    state honestly — which was the hard part. What it CANNOT do is show how
 *    many nodes each kind covers; no endpoint returns a count.
 *  · The **value-type editor** is `Dropdown` (ten types) + `SegmentedControl`
 *    (one/many) + a repeating option-row list built from `Input` + `Btn`.
 *
 * My read after building: neither needs a new DS component. What is genuinely
 * missing is narrower than a component — the "applies to nothing" warning is
 * drawn from `toneColors("warning")` inline, where a named DS treatment for
 * "this configuration is inert" would be the reusable thing.
 */

/** The ten live field types. CHECK-constrained, so a stored row is always one. */
const FIELD_TYPES = ["text", "textarea", "number", "email", "phone", "date", "datetime", "select", "checkbox", "file"] as const;

/** Types whose values come from `field_options` rows. */
const CHOICE_TYPES = ["select"];


/**
 * How a child node's values combine with an inherited list. A DECLARATION only
 * — nothing in the platform materializes the resolved effective value, so this
 * states intent and never previews an outcome.
 */
const INHERIT_MODES = [
  { value: "none", get label() { return i18nT("nodes.attributes.inherit.none", "none — no inheritance"); } },
  { value: "replace", get label() { return i18nT("nodes.attributes.inherit.replace", "replace — a child's values replace the inherited list"); } },
  { value: "add", get label() { return i18nT("nodes.attributes.inherit.add", "add — a child's values extend the inherited list"); } },
  { value: "narrow", get label() { return i18nT("nodes.attributes.inherit.narrow", "narrow — a child may only reduce the inherited list"); } },
];

const OPTION_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

const ENTITY_TYPES = [
  { value: "node", get label() { return i18nT("nodes.attributes.entity.node", "node"); } },
  { value: "customer", get label() { return i18nT("nodes.attributes.entity.customer", "customer"); } },
  { value: "booking", get label() { return i18nT("nodes.attributes.entity.booking", "booking"); } },
  { value: "event_registration", get label() { return i18nT("nodes.attributes.entity.eventregistration", "event registration"); } },
  { value: "service", get label() { return i18nT("nodes.attributes.entity.service", "service"); } },
];

interface OptionDraft { key: string; label: string }

/* Layout (2026-09-30). The Card defaults to padding 0, so every field sat flush
   against the card's border. Both cards now take the spacing spec's "content"
   padding (24), and the form follows the Settings screens: one label style,
   fields 20 apart, helper text 6 under its control, and the action on its own
   footer row. */
const groupLabel: CSSProperties = { fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n2, marginBottom: T.space[2] };
const helpLine: CSSProperties = { fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.5, marginTop: 6 };
const cardTitle: CSSProperties = { fontSize: 15, fontWeight: 700, color: T.n1, margin: 0 };

export default function AttributesScreen() {
  const t = useT();
  const nav = useNav();
  const { kinds, loading: kindsLoading } = useNodeKinds();
  const [entityType, setEntityType] = useState("node");
  const [label, setLabel] = useState("");
  const [fieldKey, setFieldKey] = useState("");
  const [fieldType, setFieldType] = useState<string>("text");
  const [cardinality, setCardinality] = useState<"one" | "many">("one");
  const [inheritMode, setInheritMode] = useState("none");
  const [required, setRequired] = useState(false);
  const [visibleToPublic, setVisibleToPublic] = useState(true);
  const [options, setOptions] = useState<OptionDraft[]>([]);
  const [appliesToKinds, setAppliesToKinds] = useState<string[]>([]);

  const isChoice = CHOICE_TYPES.includes(fieldType);
  const activeKinds = kinds.filter((k) => k.active);
  const warn = toneColors("warning");

  const keyIssue = useMemo(() => {
    if (!fieldKey) return null;
    if (!FIELD_KEY_PATTERN.test(fieldKey)) return "Lower-case, starting with a letter; letters, digits and underscores only.";
    return null;
  }, [fieldKey]);

  const optionIssue = useMemo(() => {
    if (!isChoice) return null;
    if (!options.length) return "A select needs at least one option.";
    const keys = options.map((o) => o.key);
    if (keys.some((k) => !OPTION_KEY_PATTERN.test(k))) return "Option keys are lower-case alphanumeric with _ or -.";
    if (new Set(keys).size !== keys.length) return "Option keys must be unique.";
    if (options.some((o) => !o.label.trim())) return "Every option needs a label.";
    return null;
  }, [isChoice, options]);

  const save = () => {
    if (keyIssue) { toast({ tone: "error", title: t("nodes.attributesScreen.title", "That key isn't valid"), subtitle: keyIssue }); return; }
    if (!label.trim()) { toast({ tone: "error", title: t("nodes.attributesScreen.title2", "A label is required") }); return; }
    if (optionIssue) { toast({ tone: "error", title: t("nodes.attributesScreen.title3", "Fix the options first"), subtitle: optionIssue }); return; }
    if (!isChoice && options.length) {
      toast({ tone: "error", title: t("nodes.attributesScreen.title4", "Options only belong on a select"), subtitle: t("nodes.attributesScreen.subtitle", "Sending them for another type is refused.") });
      return;
    }
    toast({
      tone: "info",
      title: t("nodes.attributesScreen.title5", "Definitions have no typed client function"),
      subtitle: t("nodes.attributesScreen.subtitle2", "This validated locally and stopped at the boundary rather than reporting a save that did not happen."),
    });
  };

  return (
    <Screen rail="nodes">
      <PageHeader
        title={t("nodes.attributesScreen.title6", "Attributes")}
        subtitle={t("nodes.attributesScreen.subtitle3", "The tenant's attribute vocabulary. One definition per entity type and key, tenant-wide — a definition is a statement about the tenant, not about one node.")}
        actions={<Btn variant="secondary" onClick={() => nav.go("nodes")}>{t("nodes.attributesScreen.text2", "Organization structure")}</Btn>}
      />
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px 32px" }}>
      {/* Two cards side by side; the "Applies to" card drops under the form when the pane is narrow. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start", maxWidth: 1180 }}>
        <Card padding="content" style={{ flex: "1 1 520px", minWidth: 0 }}>
          <h2 style={{ ...cardTitle, marginBottom: 20 }}>{t("nodes.attributesScreen.text3", "New definition")}</h2>

          <div style={{ display: "grid", gap: 20, maxWidth: 560 }}>
            <Dropdown
              label={t("nodes.attributesScreen.label12", "Applies to entity")}
              options={ENTITY_TYPES}
              value={entityType}
              onChange={setEntityType}
              helperText={t("nodes.attributesScreen.helperText", "What this attribute describes — a branch, a customer, a booking.")}
            />

            <Input label={t("nodes.attributesScreen.label13", "Label")} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("nodes.attributesScreen.placeholder", "e.g. Step-free access")} full />

            <Input
              label={t("nodes.attributesScreen.label14", "Key")}
              value={fieldKey}
              onChange={(e) => setFieldKey(e.target.value)}
              placeholder={t("nodes.attributesScreen.placeholder2", "step_free_access")}
              error={keyIssue ?? undefined}
              helperText={keyIssue ? undefined : t("nodes.attributesScreen.helperText2", "Used in exports, filters and integrations. Permanent once created — the label can still change.")}
              full
            />

            {/* Type and cardinality are SEPARATE controls because they are
                separate facts. This is the §3.1 point that most changes the UI:
                "several values" is not a type. */}
            <Dropdown
              label={t("nodes.attributesScreen.label15", "Value type")}
              options={FIELD_TYPES.map((v) => ({ value: v, label: v }))}
              value={fieldType}
              onChange={setFieldType}
              helperText={t("nodes.attributesScreen.helperText3", "Pick a select when the answer should come from a fixed list of options.")}
            />

            <div>
              <div style={groupLabel}>{t("nodes.attributesScreen.text4", "How many values")}</div>
              <SegmentedControl
                options={[{ value: "one", label: t("nodes.attributesScreen.label10", "One") }, { value: "many", label: t("nodes.attributesScreen.label11", "Several") }]}
                value={cardinality}
                onChange={(v) => setCardinality(v as "one" | "many")}
                ariaLabel={t("nodes.attributesScreen.ariaLabel", "How many values")}
              />
              <div style={helpLine}>{t("nodes.attributesScreen.text5", "Whether one answer is allowed, or several at once.")}</div>
            </div>

            {isChoice ? (
              <div>
                <div style={groupLabel}>{t("nodes.attributesScreen.text6", "Options")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {options.map((o, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Input value={o.key} onChange={(e) => setOptions((xs) => xs.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} placeholder={t("nodes.attributesScreen.placeholder3", "key")} size="sm" style={{ width: 160, flexShrink: 0 }} />
                      <Input value={o.label} onChange={(e) => setOptions((xs) => xs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder={t("nodes.attributesScreen.placeholder4", "Label")} size="sm" full />
                      <Btn variant="ghost" size="sm" onClick={() => setOptions((xs) => xs.filter((_, j) => j !== i))} aria-label={t("nodes.attributesScreen.ariaLabel2", "Remove option")}><Icon.x size={13} /></Btn>
                    </div>
                  ))}
                  <div><Btn variant="ghost" size="sm" onClick={() => setOptions((xs) => [...xs, { key: "", label: "" }])}>{t("nodes.attributesScreen.text7", "Add option")}</Btn></div>
                </div>
                <div style={{ ...helpLine, color: optionIssue ? warn.fg : T.soft }}>
                  {optionIssue ?? t("nodes.attributesScreen.text8", "Labels can change later; keys cannot. This is the complete list of options.")}
                </div>
              </div>
            ) : null}

            <Dropdown
              label={t("nodes.attributesScreen.label16", "Inheritance")}
              options={INHERIT_MODES}
              value={inheritMode}
              onChange={setInheritMode}
              helperText={t("nodes.attributesScreen.helperText4", "What happens to this attribute further down the tree when a branch sets its own value.")}
            />

            <div>
              <div style={groupLabel}>{t("nodes.attributesScreen.text9", "Rules")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Checkbox checked={required} onChange={setRequired} label={t("nodes.attributesScreen.label17", "Required")} />
                <Checkbox checked={visibleToPublic} onChange={setVisibleToPublic} label={t("nodes.attributesScreen.label18", "Visible on public surfaces")} />
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
            <Btn onClick={save}>{t("nodes.attributesScreen.text10", "Create definition")}</Btn>
          </div>
        </Card>

        {/* The applicability editor. The §3.1 rule it has to carry: EMPTY means
            NOTHING, and that has to look like a decision rather than a blank. */}
        <Card padding="content" style={{ flex: "1 1 320px", maxWidth: 420, minWidth: 0 }}>
          <h2 style={{ ...cardTitle, marginBottom: 4 }}>{t("nodes.attributesScreen.text11", "Applies to")}</h2>
          <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, lineHeight: 1.5, marginBottom: 16 }}>
            {t("nodes.attributesScreen.text12", "Which node kinds carry this attribute.")}</div>

          {appliesToKinds.length === 0 ? (
            required ? (
              <Banner tone="warning" title={t("nodes.attributesScreen.title7", "Required, and applies to nothing")} style={{ marginBottom: 16 }}>
                {t("nodes.attributesScreen.text13", "A required attribute that reaches no kind can never be satisfied. This one needs a kind, or it should not be required.")}</Banner>
            ) : (
              <Banner effect="inert" title={t("nodes.attributesScreen.title8", "Applies to nothing")} style={{ marginBottom: 16 }}>
                {t("nodes.attributesScreen.text14", "Valid, and takes effect nowhere. An empty list is not \"everywhere\" — removing the last kind narrows a definition to nothing rather than widening it across the tenant.")}</Banner>
            )
          ) : null}

          {kindsLoading ? (
            <div style={{ fontSize: 12.5, color: T.soft }}>{t("nodes.attributesScreen.text15", "Loading kinds…")}</div>
          ) : activeKinds.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {activeKinds.map((k) => {
                const on = appliesToKinds.includes(k.id);
                return (
                  <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", border: `1px solid ${on ? T.p1 : T.border}`, borderRadius: 8 }}>
                    <Checkbox
                      checked={on}
                      onChange={(next) => setAppliesToKinds((xs) => (next ? [...xs, k.id] : xs.filter((x) => x !== k.id)))}
                      aria-label={k.label}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{k.label}</span>
                      <span style={{ fontSize: 11, color: T.soft, marginLeft: 6, fontFamily: T.fontMono }}>{k.key}</span>
                    </span>
                    {/* `class` is guidance metadata — it says whether an address
                        belongs on a node. No authorization, no structural
                        meaning, so it shows as a hint and never as a level. */}
                    {k.class ? <Chip>{k.class}</Chip> : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              title={t("nodes.attributes.noKindsTitle", "No kinds to choose from")}
              body={t("nodes.attributes.noKindsBody", "The node-kinds endpoint is live but has no mock, so this list is empty at design time rather than broken. Note the relation stores kind IDs, and those ids are only obtainable from that endpoint.")}
            />
          )}
        </Card>
      </div>
      </div>
    </Screen>
  );
}
